import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Field, Icon, Input, Modal, Spinner, Textarea, useToast
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { brl, datePt, todayIso } from '../lib/format';
import { usePageMeta } from '../lib/seo';
import { useBrandOverride } from '../lib/brand';
import { applyTheme, currentTheme } from '../lib/theme';
import { tr } from '../lib/i18n';

interface ProfileService {
  id: string;
  name: string;
  description: string | null;
  imagePath: string | null;
  priceCents: number;
  durationMinutes: number;
  categoryLabel: string | null;
}

interface ProfileReview {
  rating: number;
  comment: string | null;
  created_at: string;
  customerName: string;
}

interface ProfileDayHours {
  weekday: number;
  opensAt: string;
  closesAt: string;
  breakStart: string | null;
  breakEnd: string | null;
}

interface ProfileSocial {
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  website: string | null;
}

interface ProfileResponse {
  professional: {
    id: string;
    name: string;
    slug: string;
    title: string | null;
    bio: string | null;
    specialties: unknown[];
    photoPath: string | null;
    whatsapp: string | null;
  };
  business: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    headline: string | null;
    address: string | null;
    policies: string | null;
    gallery: unknown[];
    theme: string | null;
    logoPath: string | null;
    coverPath: string | null;
    category: string | null;
    categoryLabel: string | null;
    social: ProfileSocial;
    timezone: string | null;
    hours: ProfileDayHours[];
  };
  services: ProfileService[];
  reviews: { average: number | null; count: number; items: ProfileReview[] };
}

interface Slot {
  start: string;
  end: string;
}

type Step = 'service' | 'date' | 'time' | 'details';

interface CustomerForm {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SECONDARY_LINK =
  'inline-flex items-center justify-center gap-2 rounded-full border border-line bg-card px-5 py-3 text-sm font-semibold text-ink transition-all duration-150 hover:bg-surface active:scale-[0.97]';

function weekdayIndexInTz(timeZone: string | null): number {
  try {
    const label = new Intl.DateTimeFormat('en-US', { timeZone: timeZone ?? undefined, weekday: 'short' }).format(new Date());
    const index = WEEKDAY_SHORT.indexOf(label);
    return index === -1 ? new Date().getDay() : index;
  } catch {
    return new Date().getDay();
  }
}

function minutesInTz(timeZone: string | null): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone ?? undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    return hour * 60 + minute;
  } catch {
    return new Date().getHours() * 60 + new Date().getMinutes();
  }
}

function toMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function PublicProfilePage(): JSX.Element {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('service');
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [time, setTime] = useState('');
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [policy, setPolicy] = useState<string | null>(null);
  const [customer, setCustomer] = useState<CustomerForm>({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const brand = profile ? { name: profile.business.name, logoPath: profile.business.logoPath ?? null } : null;
  useBrandOverride(brand);

  const businessTheme = profile?.business.theme ?? null;
  useEffect(() => {
    if (!businessTheme) return;
    const previous = currentTheme();
    applyTheme(businessTheme, false);
    return () => {
      applyTheme(previous, false);
    };
  }, [businessTheme]);

  const shareImage = profile
    ? new URL(profile.business.coverPath ?? profile.professional.photoPath ?? '/brand/logo-mark.png', window.location.origin).toString()
    : null;

  usePageMeta(
    profile ? `${profile.professional.name} · ${profile.business.name}` : tr('Perfil profissional'),
    profile?.business.headline || profile?.professional.bio || tr('Veja serviços, horários e agende com este profissional pela AgendaPro.'),
    `/profissional/${slug}`,
    shareImage
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<ProfileResponse>(`/api/public/professionals/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (alive) setProfile(res);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : tr('Perfil não encontrado.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const loadAvailability = useCallback(
    async (nextDate: string, nextServiceId: string) => {
      if (!slug || !nextServiceId || !nextDate) return;
      setAvailabilityLoading(true);
      setAvailabilityError(null);
      setTime('');
      try {
        const res = await api.get<{ slots: Slot[]; cancellationPolicy: string }>(
          `/api/public/professionals/${encodeURIComponent(slug)}/availability?date=${nextDate}&serviceId=${nextServiceId}`
        );
        setSlots(res.slots);
        setPolicy(res.cancellationPolicy);
      } catch (err: unknown) {
        setSlots([]);
        setAvailabilityError(err instanceof ApiError ? err.message : tr('Não foi possível carregar os horários.'));
      } finally {
        setAvailabilityLoading(false);
      }
    },
    [slug]
  );

  const selectedService = profile?.services.find((s) => s.id === serviceId) ?? null;

  const openBooking = () => {
    setStep('service');
    setServiceId('');
    setDate(todayIso());
    setSlots([]);
    setTime('');
    setPolicy(null);
    setSubmitError(null);
    setOpen(true);
  };

  const goToDate = () => {
    setStep('date');
  };

  const goToTime = () => {
    setStep('time');
    void loadAvailability(date, serviceId);
  };

  const changeDate = (next: string) => {
    setDate(next);
    void loadAvailability(next, serviceId);
  };

  const goToDetails = () => {
    setStep('details');
    setSubmitError(null);
  };

  const shareProfile = async () => {
    const url = window.location.href;
    const title = profile ? `${profile.professional.name} · ${profile.business.name}` : document.title;
    const text = profile?.business.headline ?? profile?.professional.title ?? '';
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({ title, text, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast(tr('Link copiado para compartilhar.'), 'success');
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(url);
        toast(tr('Link copiado para compartilhar.'), 'success');
      } catch {
        toast(tr('Não foi possível compartilhar agora.'), 'info');
      }
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !date || !time || !slug) return;
    if (!customer.email.trim() && !customer.phone.trim()) {
      setSubmitError(tr('Informe um e-mail ou telefone de contato.'));
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const idempotencyKey = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const res = await api.post<{
        appointment: { code: string; businessName: string; message: string };
        whatsapp: string | null;
        calendarUrl: string;
      }>(
        `/api/public/professionals/${encodeURIComponent(slug)}/book`,
        {
          serviceId,
          date,
          startTime: time,
          customer: {
            name: customer.name.trim(),
            email: customer.email.trim(),
            phone: customer.phone.trim(),
            notes: customer.notes.trim()
          }
        },
        { idempotencyKey }
      );
      setOpen(false);
      toast('Agendamento registrado.', 'success');
      navigate(`/agendamento/confirmado/${res.appointment.code}`, {
        state: { whatsapp: res.whatsapp, calendarUrl: res.calendarUrl, message: res.appointment.message }
      });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'slot_taken') {
        toast(tr('Este horário acabou de ser tomado. Escolha outro disponível.'), 'info');
        setStep('time');
        void loadAvailability(date, serviceId);
      } else if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError(tr('Não foi possível concluir o agendamento. Tente novamente.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex w-full max-w-content justify-center px-4 py-20">
        <Spinner label={tr("Carregando perfil")} />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="mx-auto w-full max-w-content px-4 py-20">
        <Alert tone="danger" title={tr("Perfil indisponível")}>
          {error ?? tr('Este perfil não está disponível no momento.')}
        </Alert>
        <Link to="/busca" className="mt-4 inline-block">
          <Button variant="secondary" icon={<Icon name="back" />}>{tr("Voltar para a busca")}</Button>
        </Link>
      </div>
    );
  }

  const { professional, business, services, reviews } = profile;
  const specialties = professional.specialties.map((s) => String(s)).filter(Boolean);
  const portfolio = business.gallery.map((g) => String(g)).filter((s) => s.length > 0);
  const socialLinks = [
    business.social.instagram ? { href: business.social.instagram, icon: 'instagram', label: 'Instagram' } : null,
    business.social.facebook ? { href: business.social.facebook, icon: 'facebook', label: 'Facebook' } : null,
    business.social.tiktok ? { href: business.social.tiktok, icon: 'tiktok', label: 'TikTok' } : null,
    business.social.website ? { href: business.social.website, icon: 'link', label: tr('Site') } : null
  ].filter((item): item is { href: string; icon: string; label: string } => item !== null);

  const mapUrl = business.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`
    : null;

  const hoursByDay = new Map<number, ProfileDayHours[]>();
  for (const entry of business.hours) {
    const list = hoursByDay.get(entry.weekday) ?? [];
    list.push(entry);
    hoursByDay.set(entry.weekday, list);
  }
  const todayWeekday = weekdayIndexInTz(business.timezone);
  const nowMinutes = minutesInTz(business.timezone);
  const todayEntries = hoursByDay.get(todayWeekday) ?? [];
  const isOpenNow = todayEntries.some((entry) => {
    const inside = nowMinutes >= toMinutes(entry.opensAt) && nowMinutes < toMinutes(entry.closesAt);
    const onBreak = Boolean(
      entry.breakStart && entry.breakEnd &&
      nowMinutes >= toMinutes(entry.breakStart) && nowMinutes < toMinutes(entry.breakEnd)
    );
    return inside && !onBreak;
  });

  const hasOnlineServices = services.length > 0;
  const stats = [
    reviews.count ? { label: tr('Nota'), value: reviews.average === null ? '—' : reviews.average.toFixed(1) } : null,
    reviews.count ? { label: tr('Avaliações'), value: String(reviews.count) } : null,
    hasOnlineServices ? { label: tr('Serviços'), value: String(services.length) } : null,
    portfolio.length ? { label: tr('Fotos'), value: String(portfolio.length) } : null
  ].filter((stat): stat is { label: string; value: string } => stat !== null);

  const sections = [
    professional.bio || business.description ? { id: 'sobre', label: tr('Sobre') } : null,
    services.length ? { id: 'servicos', label: tr('Serviços') } : null,
    portfolio.length ? { id: 'portfolio', label: tr('Portfólio') } : null,
    reviews.count ? { id: 'avaliacoes', label: tr('Avaliações') } : null,
    business.hours.length ? { id: 'horarios', label: tr('Horários') } : null,
    business.address ? { id: 'local', label: tr('Local') } : null
  ].filter((item): item is { id: string; label: string } => item !== null);

  const whatsappLink = professional.whatsapp ? (
    <a href={professional.whatsapp} target="_blank" rel="noopener noreferrer" className={SECONDARY_LINK}>
      <Icon name="whatsapp" size={18} /> {tr('WhatsApp')}
    </a>
  ) : null;

  const shareButton = (
    <Button variant="secondary" onClick={() => void shareProfile()} icon={<Icon name="share" />}>
      {tr('Compartilhar')}
    </Button>
  );

  return (
    <div className="mx-auto w-full max-w-content px-4 pb-16 pt-6">
      <Link to="/busca" className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-ink">
        <Icon name="back" size={16} /> {tr("Voltar para a busca")}
      </Link>

      <section className="mt-3 overflow-hidden rounded-[28px] border border-line bg-card shadow-md">
        <div
          className="relative h-36 overflow-hidden sm:h-52 lg:h-60"
          style={business.coverPath ? undefined : { background: 'linear-gradient(135deg, var(--nl-accent) 0%, var(--nl-graphite) 100%)' }}
        >
          {business.coverPath ? (
            <img src={business.coverPath} alt={tr('Capa de {name}', { name: business.name })} className="h-full w-full object-cover" />
          ) : null}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(120% 90% at 12% 0%, rgba(255,255,255,.24), transparent 58%)' }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,.34), transparent 55%)' }}
          />
        </div>

        <div className="px-5 pb-5 sm:px-8 sm:pb-6">
          <div className="-mt-12 flex items-end justify-between gap-3 sm:-mt-16">
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border-4 border-card bg-accent shadow-md sm:h-32 sm:w-32">
              {professional.photoPath ? (
                <img
                  src={professional.photoPath}
                  alt={`Foto de ${professional.name}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span aria-hidden="true" className="flex h-full w-full items-center justify-center font-display text-3xl font-semibold text-on-accent sm:text-4xl">
                  {initialsOf(professional.name)}
                </span>
              )}
            </div>
            <div className="hidden flex-wrap justify-end gap-2 pb-1 sm:flex">
              <Button onClick={openBooking} disabled={!hasOnlineServices} icon={<Icon name="calendar" />}>{tr("Agendar")}</Button>
              {whatsappLink}
              {shareButton}
            </div>
          </div>

          <div className="mt-4">
            <h1 className="font-display text-3xl font-semibold leading-tight text-ink sm:text-4xl">{professional.name}</h1>
            {professional.title ? <p className="mt-1 text-base text-muted sm:text-lg">{professional.title}</p> : null}
            {business.headline ? (
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink sm:text-base">{business.headline}</p>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted">
              <span className="inline-flex items-center gap-1.5">
                <Icon name="user" size={15} /> {tr("em")}{' '}
                <span className="font-medium text-ink">{business.name}</span>
              </span>
              {business.categoryLabel ? (
                <span className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-semibold text-ink">
                  {tr(business.categoryLabel)}
                </span>
              ) : null}
              {business.address && mapUrl ? (
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-ink"
                >
                  <Icon name="pin" size={15} /> {business.address}
                </a>
              ) : null}
            </div>

            <div className="mt-3 space-y-2 sm:hidden">
              <Button onClick={openBooking} disabled={!hasOnlineServices} icon={<Icon name="calendar" />} className="w-full">{tr("Agendar")}</Button>
              <div className="grid grid-cols-2 gap-2">
                {whatsappLink}
                <Button variant="secondary" onClick={() => void shareProfile()} icon={<Icon name="share" />} className="w-full">
                  {tr('Compartilhar')}
                </Button>
              </div>
            </div>
            {!hasOnlineServices ? (
              <p className="mt-2 text-xs text-muted">{tr('Agendamento online indisponível no momento.')}</p>
            ) : null}

            {stats.length ? (
              <dl className="mt-5 grid grid-cols-2 gap-1 rounded-2xl border border-line bg-surface px-2 py-2 sm:grid-cols-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="rounded-xl px-3 py-2 text-center">
                    <dt className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">{stat.label}</dt>
                    <dd className="font-display text-xl font-semibold text-ink">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {socialLinks.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-surface"
                  >
                    <Icon name={link.icon} size={15} /> {link.label}
                  </a>
                ))}
              </div>
            ) : null}

            {specialties.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {specialties.map((s) => (
                  <span key={s} className="rounded-full bg-surface px-3 py-1 text-xs font-medium text-ink">{s}</span>
                ))}
              </div>
            ) : null}

            {professional.whatsapp ? (
              <p className="mt-3 text-xs text-muted">
                {tr("O botão WhatsApp abre a conversa no seu aplicativo — a AgendaPro não envia mensagens automáticas.")}
              </p>
            ) : null}
          </div>

          {sections.length ? (
            <nav aria-label={tr('Seções do perfil')} className="-mx-1 mt-5 flex gap-2 overflow-x-auto px-1 pb-1">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="whitespace-nowrap rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-surface"
                >
                  {section.label}
                </a>
              ))}
            </nav>
          ) : null}
        </div>
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {professional.bio || business.description ? (
            <Card as="section" id="sobre" className="scroll-mt-24">
              <h2 className="font-display text-xl font-semibold">{tr("Sobre")}</h2>
              {professional.bio ? <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted">{professional.bio}</p> : null}
              {business.description ? (
                <p className="mt-3 whitespace-pre-line border-t border-line pt-3 text-sm leading-relaxed text-muted">
                  {business.description}
                </p>
              ) : null}
            </Card>
          ) : null}

          {hasOnlineServices ? (
            <Card as="section" id="servicos" className="scroll-mt-24">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-display text-xl font-semibold">{tr("Serviços")}</h2>
                <span className="text-xs text-muted">{services.length} {tr('disponíveis para agendamento online')}</span>
              </div>
              <ul className="mt-3 divide-y divide-line">
                {services.map((s) => (
                  <li key={s.id} className="flex items-start gap-3 py-3">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface text-muted">
                      {s.imagePath ? <img src={s.imagePath} alt="" className="h-full w-full object-cover" /> : <Icon name="scissors" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ink">{s.name}</p>
                      {s.categoryLabel ? <p className="text-xs text-muted">{s.categoryLabel}</p> : null}
                      {s.description ? <p className="mt-1 text-sm text-muted">{s.description}</p> : null}
                      <p className="mt-1 flex items-center gap-1 text-sm text-muted"><Icon name="clock" size={14} /> {s.durationMinutes} {tr("min")}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-ink">{brl(s.priceCents)}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setServiceId(s.id);
                          setStep('date');
                          setSlots([]);
                          setTime('');
                          setSubmitError(null);
                          setOpen(true);
                        }}
                        className="mt-1 text-sm font-medium text-graphite underline"
                      >
                        {tr("Agendar")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {portfolio.length ? (
            <Card as="section" id="portfolio" className="scroll-mt-24">
              <h2 className="font-display text-xl font-semibold">{tr("Portfólio")}</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {portfolio.map((src, i) => (
                  <a
                    key={src}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="overflow-hidden rounded-md"
                    aria-label={tr('Abrir foto {n} do portfólio de {name}', { n: i + 1, name: professional.name })}
                  >
                    <img src={src} alt={tr('Trabalho {n} de {name}', { n: i + 1, name: professional.name })} className="aspect-square w-full object-cover transition-transform hover:scale-[1.02]" />
                  </a>
                ))}
              </div>
            </Card>
          ) : null}

          {reviews.count > 0 ? (
            <Card as="section" id="avaliacoes" className="scroll-mt-24">
              <h2 className="font-display text-xl font-semibold">{tr("Avaliações")}</h2>
              {reviews.average !== null ? (
                <div className="mt-3 flex items-center gap-4 rounded-2xl bg-surface px-4 py-3">
                  <p className="font-display text-3xl font-semibold text-ink">{reviews.average.toFixed(1)}</p>
                  <div>
                    <p className="text-sm font-medium text-ink">
                      {'★'.repeat(Math.round(reviews.average))}
                      <span className="text-muted">{'★'.repeat(Math.max(0, 5 - Math.round(reviews.average)))}</span>
                    </p>
                    <p className="text-xs text-muted">{reviews.count} {tr('avaliações publicadas')}</p>
                  </div>
                </div>
              ) : null}
              <ul className="mt-3 space-y-3">
                {reviews.items.map((r, i) => (
                  <li key={`${r.created_at}-${i}`} className="border-b border-line pb-3 last:border-0">
                    <p className="text-sm font-medium text-ink">
                      {'★'.repeat(r.rating)}{'☆'.repeat(Math.max(0, 5 - r.rating))} · {r.customerName}
                    </p>
                    {r.comment ? <p className="mt-1 text-sm text-muted">{r.comment}</p> : null}
                    <p className="mt-1 text-xs text-muted">{datePt(r.created_at)}</p>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>

        <aside className="space-y-6">
          {business.hours.length ? (
            <Card as="section" id="horarios" className="scroll-mt-24">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-lg font-semibold">{tr("Horários")}</h2>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    isOpenNow ? 'bg-positive-soft text-positive-ink' : 'bg-surface text-muted'
                  }`}
                >
                  {isOpenNow ? tr('Aberto agora') : tr('Fechado agora')}
                </span>
              </div>
              <ul className="mt-3 space-y-1">
                {WEEKDAYS.map((day, index) => {
                  const entries = hoursByDay.get(index) ?? [];
                  const isToday = index === todayWeekday;
                  return (
                    <li
                      key={day}
                      className={`rounded-xl px-3 py-2 text-sm ${isToday ? 'bg-surface font-semibold text-ink' : 'text-muted'}`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span>
                          {tr(day)}
                          {isToday ? <span className="ml-2 text-[0.7rem] uppercase tracking-wide text-muted">{tr('hoje')}</span> : null}
                        </span>
                        <span className="text-right">
                          {entries.length === 0 ? tr('Fechado') : entries.map((e) => `${e.opensAt}–${e.closesAt}`).join(' · ')}
                        </span>
                      </div>
                      {entries.some((e) => e.breakStart && e.breakEnd) ? (
                        <p className="mt-0.5 text-[11px] font-normal text-muted">
                          {entries
                            .filter((e) => e.breakStart && e.breakEnd)
                            .map((e) => `${tr('Intervalo')} ${e.breakStart}–${e.breakEnd}`)
                            .join(' · ')}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}

          <Card as="section" id="local" className="scroll-mt-24">
            <h2 className="font-display text-lg font-semibold">{tr("Local")}</h2>
            {business.address ? (
              <>
                <p className="mt-2 flex gap-2 text-sm text-muted">
                  <Icon name="pin" size={16} /> <span>{business.address}</span>
                </p>
                {mapUrl ? (
                  <a href={mapUrl} target="_blank" rel="noopener noreferrer" className={`${SECONDARY_LINK} mt-3 w-full`}>
                    <Icon name="map" size={18} /> {tr('Abrir no mapa')}
                  </a>
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-sm text-muted">{tr("Endereço não informado.")}</p>
            )}
          </Card>

          {business.policies?.trim() ? (
            <Card as="section" id="politicas" className="scroll-mt-24">
              <h2 className="font-display text-lg font-semibold">{tr("Políticas")}</h2>
              <p className="mt-2 whitespace-pre-line text-sm text-muted">{business.policies}</p>
            </Card>
          ) : null}
        </aside>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={tr("Agendar horário")}
        footer={
          step === 'service' ? (
            <>
              <Button variant="ghost" type="button" className="flex-1 sm:flex-none" onClick={() => setOpen(false)}>{tr("Cancelar")}</Button>
              <Button type="button" className="flex-1 sm:flex-none" disabled={!serviceId} onClick={goToDate}>{tr("Continuar")}</Button>
            </>
          ) : step === 'date' ? (
            <>
              <Button variant="ghost" type="button" className="flex-1 sm:flex-none" onClick={() => setStep('service')}>{tr("Voltar")}</Button>
              <Button type="button" className="flex-1 sm:flex-none" onClick={goToTime}>{tr("Ver horários")}</Button>
            </>
          ) : step === 'time' ? (
            <>
              <Button variant="ghost" type="button" className="flex-1 sm:flex-none" onClick={() => setStep('date')}>{tr("Voltar")}</Button>
              <Button type="button" className="flex-1 sm:flex-none" disabled={!time || availabilityLoading} onClick={goToDetails}>{tr("Continuar")}</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" type="button" className="flex-1 sm:flex-none" onClick={() => setStep('time')}>{tr("Voltar")}</Button>
              <Button type="submit" form="booking-form" className="flex-1 sm:flex-none" loading={submitting}>{tr("Confirmar agendamento")}</Button>
            </>
          )
        }
      >
        <div aria-live="polite">
          <StepIndicator step={step} />

          {step === 'service' ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-ink">{tr("1. Escolha um serviço")}</p>
              {services.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setServiceId(s.id);
                    goToDate();
                  }}
                  className={`w-full rounded-2xl border p-3 text-left transition-all ${serviceId === s.id ? 'border-accent bg-surface shadow-[0_10px_24px_-20px_var(--nl-accent)]' : 'border-line hover:bg-surface'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink sm:text-base">{s.name}</span>
                    <span className="shrink-0 text-xs text-muted sm:text-sm">{s.durationMinutes} {tr("min ·")} {brl(s.priceCents)}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {step === 'date' ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-ink">{tr("2. Escolha a data")}</p>
              <Field label={tr("Data desejada")} hint="Mostramos apenas os dias com horários livres.">
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    min={todayIso()}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : null}

          {step === 'time' ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-ink">{tr("3. Escolha o horário")}</p>
              <div className="flex items-center gap-2">
                <Input type="date" min={todayIso()} value={date} onChange={(e) => changeDate(e.target.value)} aria-label={tr("Alterar data dos horários")} />
              </div>

              {availabilityLoading ? <Spinner label={tr("Buscando horários")} /> : null}
              {availabilityError ? <Alert tone="danger" title={tr("Horários")}>{availabilityError}</Alert> : null}

              {!availabilityLoading && !availabilityError && slots.length === 0 ? (
                <p className="rounded-md bg-surface px-3 py-2 text-sm text-muted">
                  {tr("Nenhum horário livre nesta data. Tente outro dia.")}
                </p>
              ) : null}

              {slots.length > 0 ? (
                <div className="flex flex-wrap gap-2" role="group" aria-label={tr("Horários disponíveis")}>
                  {slots.map((s) => (
                    <button
                      key={s.start}
                      type="button"
                      onClick={() => setTime(s.start)}
                      aria-pressed={time === s.start}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${time === s.start ? 'border-transparent bg-accent text-on-accent shadow-[0_10px_20px_-14px_var(--nl-accent)]' : 'border-line hover:bg-surface'}`}
                    >
                      {s.start}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 'details' && selectedService ? (
            <form id="booking-form" onSubmit={confirm} className="space-y-3">
              <div className="rounded-md bg-surface p-3 text-sm">
                <p className="font-medium text-ink">{selectedService.name}</p>
                <p className="text-muted">{datePt(date)} {tr("às")} {time} · {selectedService.durationMinutes} {tr("min ·")} {brl(selectedService.priceCents)}</p>
                {policy ? <p className="mt-1 text-xs text-muted">{tr("Cancelamento:")} {policy}</p> : null}
              </div>

              <Field label={tr("Seu nome")} required>
                {(props) => (
                  <Input {...props} value={customer.name} required onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
                )}
              </Field>
              <Field label={tr("E-mail")} hint="Informe e-mail ou telefone.">
                {(props) => (
                  <Input {...props} type="email" value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} />
                )}
              </Field>
              <Field label={tr("Telefone / WhatsApp")} hint="Usamos para confirmar o agendamento.">
                {(props) => (
                  <Input {...props} type="tel" value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} placeholder="(11) 90000-0000" />
                )}
              </Field>
              <Field label={tr("Observações")}>
                {(props) => (
                  <Textarea {...props} rows={3} value={customer.notes} onChange={(e) => setCustomer((c) => ({ ...c, notes: e.target.value }))} />
                )}
              </Field>

              {submitError ? <Alert tone="danger" title={tr("Não foi possível agendar")}>{submitError}</Alert> : null}

              <p className="text-xs text-muted">{tr("Você não precisa criar conta para agendar. Não cobramos nada aqui.")}</p>
            </form>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

function StepIndicator({ step }: { step: Step }): JSX.Element {
  const order: { key: Step; label: string }[] = [
    { key: 'service', label: 'Serviço' },
    { key: 'date', label: 'Data' },
    { key: 'time', label: 'Horário' },
    { key: 'details', label: 'Dados' }
  ];
  const currentIndex = order.findIndex((o) => o.key === step);
  return (
    <div>
      <p className="sr-only">{tr("Etapa")} {currentIndex + 1} {tr("de")} {order.length}</p>
      <ol className="mb-4 grid grid-cols-4 gap-1 text-center">
        {order.map((o, i) => {
          const done = i < currentIndex;
          const current = i === currentIndex;
          return (
            <li key={o.key} className="flex min-w-0 flex-col items-center gap-1" aria-current={current ? 'step' : undefined}>
              <span
                aria-hidden="true"
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${done || current ? 'bg-accent text-on-accent' : 'bg-surface text-muted'}`}
              >
                {done ? <Icon name="check" size={13} /> : i + 1}
              </span>
              <span className={`w-full truncate text-[11px] ${current ? 'font-semibold text-ink' : 'text-muted'}`}>
                {tr(o.label)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
